import { useEffect } from 'react';

/**
 * Screen Wake Lock: la pantalla no se apaga mientras reproduce.
 *
 * El auto-advance del fin de capítulo y el reloj del player viven en timers
 * de foreground: si el celu apaga la pantalla, el próximo capítulo no
 * arranca. Mientras hay reproducción mantenemos la pantalla despierta; al
 * pausar (incluido el sleep timer, que pausa) se libera el lock y el
 * teléfono vuelve a dormirse solo. Best-effort: sin soporte (browser viejo,
 * contexto no seguro) o denegado (battery saver), todo sigue funcionando.
 */
export function useWakeLock(playing: boolean): void {
  useEffect(() => {
    if (!playing || !('wakeLock' in navigator)) return;
    let alive = true;
    let sentinel: WakeLockSentinel | null = null;
    const acquire = (): void => {
      void navigator.wakeLock
        .request('screen')
        .then((s) => {
          if (!alive) void s.release().catch(() => undefined);
          else sentinel = s;
        })
        .catch(() => {
          /* denegado → seguimos sin lock */
        });
    };
    acquire();
    // El UA libera el lock al ocultar la pestaña/app; re-adquirimos al volver.
    const onVis = (): void => {
      if (document.visibilityState === 'visible' && (sentinel?.released ?? true)) acquire();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVis);
      void sentinel?.release().catch(() => undefined);
    };
  }, [playing]);
}
